import "./LogoUploader.scss";

import { useEffect, useState } from "react";
import { useGetOrganizationSettingsQuery } from "services/misc-api";

// custom hook to handle logo fetching outside of redux
function useLogoFetcher(s3Key?: string) {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    // reset state when key changes
    setLogoUrl(null);
    setError(false);

    if (!s3Key) return;

    let isMounted = true;
    setIsLoading(true);

    const fetchLogo = async () => {
      try {
        // fetch logo directly without using redux
        const response = await fetch(`/api/v1/file_proxy?s3_key=${s3Key}`, {});

        if (!response.ok) {
          throw new Error(`Failed to load logo: ${response.status}`);
        }

        const blob = await response.blob();

        // convert blob to data URL
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve(reader.result as string);
          };
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error("Error loading logo:", error);
        throw error;
      }
    };

    fetchLogo()
      .then((dataUrl) => {
        if (isMounted) {
          setLogoUrl(dataUrl);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setError(true);
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [s3Key]);

  return { logoUrl, isLoading, error };
}

export const OrganizationLogo = () => {
  const { data: orgSettings } = useGetOrganizationSettingsQuery(undefined, {
    skip: false,
  });

  const logoS3Key = orgSettings?.document_letterhead_s3_key;

  // use custom hook for logo fetching (completely outside redux)
  const { logoUrl, isLoading, error: logoError } = useLogoFetcher(logoS3Key);

  return (
    <div className="letterhead-logo-preview">
      <div className="letterhead-logo-container">
        {logoError ? (
          <div className="no-logo-message">
            <p>Unable to load logo</p>
          </div>
        ) : logoUrl ? (
          <div className="letterhead-logo-wrapper">
            <img
              src={logoUrl}
              alt="Letterhead Logo"
              onError={() => {}}
              className="letterhead-logo-image"
            />
          </div>
        ) : logoS3Key && isLoading ? (
          <div className="loading-logo">Loading logo...</div>
        ) : (
          <div className="no-logo-message">
            <p>No logo uploaded</p>
            <p className="hint-text">Upload a logo to see the preview</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationLogo;
